import React, {useContext, useState} from "react";
import HeaderPage from "../components/HeaderPage";
import {Colors} from "../colors";
import AppConfigContext from "../../../contexts/AppConfigContext";
import {Image, StyleSheet, TouchableOpacity, View} from "react-native";
import FontAwesome from "../components/FontAwesome";
import Text from "../components/Text";
import {ColumnSpace, RowSpace} from "../components/_CommonComponents";
import {AnimatedCheckIcon} from "../components/AnimatedCheckIcon";
import {OverviewData} from "../../../rest/jtmc/models/OverviewData";
import {OrderContext} from "../../../contexts/OrderContext";
import Button from "../components/Button";
import TextInput from "../components/TextInput";
import OverviewScreenFunctions, {
    DeliveryOptions,
    FinancialDataStrings,
    PaymentOptions
} from "../../../components/screen-functions-components/OverviewScreenFunctions";
import {AppFunctionsContext} from "../../../contexts/AppFunctionsContext";
import DiscountCodeComponents from "../../../components/functional-components/DiscountCodeComponents";
import {PaymentTags} from "../../../types";

export default function Overview({navigation}: any) {
    const appConfigContext = useContext(AppConfigContext);
    const colors = Colors.instanceNoServerColor(appConfigContext.theme, appConfigContext.color);
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<PaymentTags | null>(null);
    const [finalizationData, setFinalizationData] = useState<OverviewData | null>(null);

    const orderContext = useContext(OrderContext);
    const appFunctions = useContext(AppFunctionsContext);

    const [financialDataStrings, setFinancialDataStrings] = useState<FinancialDataStrings>({
        rows: [],
        totalPayablePrice: ""
    });
    const [paymentOptions, setPaymentOptions] = useState<PaymentOptions>([]);
    const [deliveryOptions, setDeliveryOptions] = useState<DeliveryOptions>([]);

    const [description, setDescription] = useState<string>(orderContext.order?.clientDescription ?? "");

    const styles = lStyle(colors);

    const paymentOptionsViews = paymentOptions.map((item, index) => {
        return <ConditionalView key={index} condition={item.visible}>
            <PaymentItem
                icon={item.icon}
                onSelect={() => setSelectedPaymentMethod(item.tag)}
                title={item.title}
                desc={item.description}
                check={selectedPaymentMethod == item.tag}/>
        </ConditionalView>
    });

    const deliveryOptionsViews = deliveryOptions.map((item, index) => {
        return <ConditionalView key={index} condition={item.visible}>
            <DeliveryItem
                onSelect={() => orderContext.selectDelivery(item.tag)}
                title={item.title}
                desc={item.description}
                check={orderContext.order?.deliveryMethod == item.tag}/>
        </ConditionalView>
    });

    const financialRowViews = financialDataStrings.rows.map((item, index) => {
        return <ConditionalView condition={item.visible} key={index}>
            <FinancialRowView w={400} title={item.title} price={item.price}/>
            <ColumnSpace h={2}/>
        </ConditionalView>
    })

    return <HeaderPage title={"سفارش من"} scrollableContent={true}>

        <OverviewScreenFunctions selectedPaymentMethod={selectedPaymentMethod}
                                 onFinancialDataStringsChanged={setFinancialDataStrings}
                                 onFinalizationDataChanged={setFinalizationData}
                                 onDeliveryOptionsChanged={setDeliveryOptions}
                                 onPaymentOptionsChanged={setPaymentOptions}>

            <ColumnSpace h={5}/>
            <_Header title={"روش ارسال"} icon=""/>
            {deliveryOptionsViews}

            <View>
                {financialRowViews}
                <FinancialRowView w={700} title="قابل پرداخت" price={financialDataStrings.totalPayablePrice}/>
            </View>
            <ColumnSpace/>

            <_Header title={"کد تخفیف"} icon=""/>
            <View>
                <View style={styles.discountContainer}>
                    <DiscountCodeComponents orderContext={orderContext}>
                        <TextInput role="input"
                                   style={{flexGrow: 1}}
                                   placeholder="اگر کد تخفیف دارید اینجا وارد کنید"
                        />
                        <RowSpace w={10}/>
                        <DiscountButton role="button"/>
                    </DiscountCodeComponents>
                </View>
                <Text style={{marginHorizontal: 10}}
                      color={orderContext.discountCodeDetails?.applied ? colors.get("green") as string : "#f00"}>
                    {orderContext.discountCodeDetails?.message}
                </Text>
            </View>
            <ColumnSpace/>

            <_Header title={"روش پرداخت"} icon=""/>
            {paymentOptionsViews}

            <_Header title={"توضیحات سفارش"} icon=""/>
            <View style={{marginHorizontal: 10}}>
                <TextInput value={description} style={{minHeight: 80}} onChangeText={(t: string) => setDescription(t)}
                           placeholder="توضیحی برای سفارش دارید؟ اینجا بنویسید" multiline={true}/>
            </View>

            <View style={{margin: 10}}>
                <Button color={colors.get("primaryColor") as string}
                        onPress={() => {
                            appFunctions.functions.finalizeOrder(selectedPaymentMethod, description);
                        }}
                        title={"نهایی کردن خرید"}/>
            </View>
            <ColumnSpace/>

        </OverviewScreenFunctions>

    </HeaderPage>
}

function _Header({title, icon}: { title: string, icon: string }) {
    const color = Colors.instanceNoServerColor(useContext(AppConfigContext).theme, useContext(AppConfigContext).color);
    const colorPrimary = color.get("primaryColor") as string;
    return <View style={{
        flexDirection: "row",
        paddingHorizontal: 20,
        paddingVertical: 10
    }}>
        <FontAwesome icon={icon} color={colorPrimary}/>
        <RowSpace/>
        <Text color={colorPrimary} weight={600} size={14}>{title}</Text>
    </View>
}

function DiscountButton(props: any) {
    const styles = lStyle(Colors.instanceNoServerColor(useContext(AppConfigContext).theme, useContext(AppConfigContext).color));
    return <TouchableOpacity {...props} role="button" style={styles.discountButton}>
        <FontAwesome icon={""} color={"#fff"}/>
    </TouchableOpacity>
}

function DeliveryItem(props: { check: boolean, title: string, desc: string, onSelect: () => void }) {
    const colors = Colors.instanceNoServerColor(useContext(AppConfigContext).theme, useContext(AppConfigContext).color);
    const style = optionStyle(colors);
    return <TouchableOpacity style={style.container} onPress={props.onSelect}>
        <View style={{flexDirection: "row", alignItems: "center"}}>
            <RowSpace w={4}/>
            <AnimatedCheckIcon type={"radio"} color={colors.get("primaryColor") as string} check={props.check}/>
            <RowSpace w={12}/>
        </View>
        <View style={{flexGrow: 1}}>
            <Text color={colors.get("lowConText") as string} size={14} weight={600}>{props.title}</Text>
            <Text color={colors.get("lowConText") as string} size={12} weight={400}>{props.desc}</Text>
        </View>
    </TouchableOpacity>
}

function PaymentItem(props: { check: boolean, title: string, desc: string, onSelect: () => void, icon: string }) {
    const colors = Colors.instanceNoServerColor(useContext(AppConfigContext).theme, useContext(AppConfigContext).color);
    const style = optionStyle(colors);
    return <TouchableOpacity style={[style.container, {
        backgroundColor: props.check ? colors.get("selectedBackgroundPrimary") as string : colors.get("lowConBackground") as string
    }]} onPress={props.onSelect}>
        <View style={{flexDirection: "row", alignItems: "center"}}>
            <RowSpace w={4}/>
            <Image style={{width: 24, height: 24}} source={{uri: props.icon}}/>
            <RowSpace w={12}/>
        </View>
        <View style={{flexGrow: 1, justifyContent: "center"}}>
            <Text color={colors.get("lowConText") as string} size={14} weight={600}>{props.title}</Text>
            <Text color={colors.get("lowConText") as string} size={12} weight={400}>{props.desc}</Text>
        </View>
    </TouchableOpacity>
}

function FinancialRowView({title, price, w}: { title: string, price: string, w: number }) {
    return <View style={{
        flexDirection: "row",
        justifyContent: "space-between",
        marginHorizontal: 18
    }}>
        <Text size={15} weight={w}>{title}</Text>
        <Text size={15} weight={w}>{price}</Text>
    </View>
}

function ConditionalView(props: { condition: boolean | undefined, children: any }) {
    return props.condition ? <>
        {props.children}
    </> : <></>;
}

const optionStyle = (colors: Colors) => StyleSheet.create({
    container: {
        backgroundColor: colors.get("lowConBackground") as string,
        borderRadius: 8,
        flexDirection: "row",
        justifyContent: "center",
        marginHorizontal: 10,
        marginBottom: 10,
        padding: 8,
        minHeight: 56
    }
});

const lStyle = (colors: Colors) => StyleSheet.create({
    discountContainer: {
        flexDirection: "row",
        marginHorizontal: 10,
    },
    discountButton: {
        backgroundColor: colors.get("primaryColor") as string,
        borderRadius: 15,
        width: 40,
        height: 40,
        justifyContent: "center",
        alignItems: "center"
    }
});